import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FamilyKYC {
  id: string;
  org_id: string;
  family_id: string;
  legal_name: string | null;
  formation_date: string | null;
  jurisdiction: string | null;
  tax_jurisdiction: string | null;
  tax_id: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  source_of_wealth: string | null;
  estimated_net_worth: string | null;
  completed_at: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BeneficialOwner {
  id: string;
  org_id: string;
  family_id: string;
  full_name: string;
  date_of_birth: string | null;
  nationality: string | null;
  tax_id: string | null;
  ownership_percentage: number | null;
  is_control_person: boolean;
  address_line1: string | null;
  address_city: string | null;
  address_country: string | null;
  created_at: string;
}

export interface FamilyEntity {
  id: string;
  org_id: string;
  family_id: string;
  entity_type: 'trust' | 'spv' | 'holding_company' | 'foundation' | 'partnership' | 'llc' | 'other';
  name: string;
  jurisdiction: string | null;
  tax_id: string | null;
  formation_date: string | null;
  purpose: string | null;
  created_at: string;
}

export interface BankAccount {
  id: string;
  org_id: string;
  family_id: string;
  entity_id: string | null;
  bank_name: string;
  account_name: string | null;
  account_number_last4: string | null;
  routing_number: string | null;
  swift_code: string | null;
  currency: string;
  account_type: 'checking' | 'savings' | 'investment' | 'custody' | null;
  is_primary: boolean;
  created_at: string;
}

export interface KYCFormData {
  // Profile
  legal_name?: string;
  formation_date?: string;
  jurisdiction?: string;
  tax_jurisdiction?: string;
  tax_id?: string;
  // Address
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  // Source of wealth
  source_of_wealth?: string;
  estimated_net_worth?: string;
}

export function useFamilyKYC(familyId: string | null, orgId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Query KYC profile
  const {
    data: kyc,
    isLoading: kycLoading,
    error: kycError,
  } = useQuery({
    queryKey: ["family-kyc", familyId],
    queryFn: async () => {
      if (!familyId) return null;
      const { data, error } = await supabase
        .from("family_kyc")
        .select("*")
        .eq("family_id", familyId)
        .maybeSingle();
      if (error) throw error;
      return data as FamilyKYC | null;
    },
    enabled: !!familyId,
  });

  // Query beneficial owners
  const {
    data: owners = [],
    isLoading: ownersLoading,
  } = useQuery({
    queryKey: ["family-beneficial-owners", familyId],
    queryFn: async () => {
      if (!familyId) return [];
      const { data, error } = await supabase
        .from("family_beneficial_owners")
        .select("*")
        .eq("family_id", familyId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as BeneficialOwner[];
    },
    enabled: !!familyId,
  });

  // Query entities
  const {
    data: entities = [],
    isLoading: entitiesLoading,
  } = useQuery({
    queryKey: ["family-entities", familyId],
    queryFn: async () => {
      if (!familyId) return [];
      const { data, error } = await supabase
        .from("family_entities")
        .select("*")
        .eq("family_id", familyId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as FamilyEntity[];
    },
    enabled: !!familyId,
  });

  // Query bank accounts
  const {
    data: bankAccounts = [],
    isLoading: bankAccountsLoading,
  } = useQuery({
    queryKey: ["family-bank-accounts", familyId],
    queryFn: async () => {
      if (!familyId) return [];
      const { data, error } = await supabase
        .from("family_bank_accounts")
        .select("*")
        .eq("family_id", familyId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as BankAccount[];
    },
    enabled: !!familyId,
  });

  // Save/Update KYC profile
  const saveKYCMutation = useMutation({
    mutationFn: async (data: KYCFormData) => {
      if (!familyId || !orgId) throw new Error("Missing family or org ID");

      if (kyc) {
        // Update existing
        const { error } = await supabase
          .from("family_kyc")
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq("id", kyc.id);
        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from("family_kyc")
          .insert({ ...data, family_id: familyId, org_id: orgId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-kyc", familyId] });
    },
    onError: (error) => {
      toast({
        title: "Error saving KYC",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Add beneficial owner
  const addOwnerMutation = useMutation({
    mutationFn: async (data: Omit<BeneficialOwner, "id" | "org_id" | "family_id" | "created_at">) => {
      if (!familyId || !orgId) throw new Error("Missing family or org ID");
      const { error } = await supabase
        .from("family_beneficial_owners")
        .insert({ ...data, family_id: familyId, org_id: orgId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-beneficial-owners", familyId] });
    },
    onError: (error) => {
      toast({
        title: "Error adding owner",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Update beneficial owner
  const updateOwnerMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<BeneficialOwner> & { id: string }) => {
      const { error } = await supabase
        .from("family_beneficial_owners")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-beneficial-owners", familyId] });
    },
  });

  // Delete beneficial owner
  const deleteOwnerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("family_beneficial_owners")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-beneficial-owners", familyId] });
    },
  });

  // Add entity
  const addEntityMutation = useMutation({
    mutationFn: async (data: Omit<FamilyEntity, "id" | "org_id" | "family_id" | "created_at">) => {
      if (!familyId || !orgId) throw new Error("Missing family or org ID");
      const { error } = await supabase
        .from("family_entities")
        .insert({ ...data, family_id: familyId, org_id: orgId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-entities", familyId] });
    },
    onError: (error) => {
      toast({
        title: "Error adding entity",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Update entity
  const updateEntityMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<FamilyEntity> & { id: string }) => {
      const { error } = await supabase
        .from("family_entities")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-entities", familyId] });
    },
  });

  // Delete entity
  const deleteEntityMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("family_entities")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-entities", familyId] });
    },
  });

  // Add bank account
  const addBankAccountMutation = useMutation({
    mutationFn: async (data: Omit<BankAccount, "id" | "org_id" | "family_id" | "created_at">) => {
      if (!familyId || !orgId) throw new Error("Missing family or org ID");
      const { error } = await supabase
        .from("family_bank_accounts")
        .insert({ ...data, family_id: familyId, org_id: orgId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-bank-accounts", familyId] });
    },
    onError: (error) => {
      toast({
        title: "Error adding bank account",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Update bank account
  const updateBankAccountMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<BankAccount> & { id: string }) => {
      const { error } = await supabase
        .from("family_bank_accounts")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-bank-accounts", familyId] });
    },
  });

  // Delete bank account
  const deleteBankAccountMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("family_bank_accounts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-bank-accounts", familyId] });
    },
  });

  // Mark KYC as complete
  const completeKYCMutation = useMutation({
    mutationFn: async () => {
      if (!familyId || !kyc) throw new Error("No KYC record to complete");

      const { data: { user } } = await supabase.auth.getUser();

      // Update KYC record
      const { error: kycError } = await supabase
        .from("family_kyc")
        .update({
          completed_at: new Date().toISOString(),
          completed_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", kyc.id);
      if (kycError) throw kycError;

      // Update family status to data_pending
      const { error: familyError } = await supabase
        .from("families")
        .update({ onboarding_status: "data_pending" })
        .eq("id", familyId);
      if (familyError) throw familyError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-kyc", familyId] });
      queryClient.invalidateQueries({ queryKey: ["families"] });
      toast({
        title: "KYC Complete",
        description: "KYC information has been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error completing KYC",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Compute completion status
  const completionStatus = {
    hasProfile: !!(kyc?.legal_name && kyc?.jurisdiction),
    hasAddress: !!(kyc?.address_line1 && kyc?.city && kyc?.country),
    hasOwners: owners.length > 0,
    hasEntities: entities.length > 0,
    hasBankAccounts: bankAccounts.length > 0,
    isComplete: !!kyc?.completed_at,
  };

  const completionPercentage = [
    completionStatus.hasProfile,
    completionStatus.hasAddress,
    completionStatus.hasOwners,
    completionStatus.hasEntities,
    completionStatus.hasBankAccounts,
  ].filter(Boolean).length * 20;

  return {
    // Data
    kyc,
    owners,
    entities,
    bankAccounts,

    // Loading states
    isLoading: kycLoading || ownersLoading || entitiesLoading || bankAccountsLoading,
    kycLoading,
    ownersLoading,
    entitiesLoading,
    bankAccountsLoading,

    // Errors
    kycError,

    // Completion status
    completionStatus,
    completionPercentage,

    // Mutations
    saveKYC: saveKYCMutation.mutate,
    saveKYCAsync: saveKYCMutation.mutateAsync,
    isSavingKYC: saveKYCMutation.isPending,

    addOwner: addOwnerMutation.mutate,
    updateOwner: updateOwnerMutation.mutate,
    deleteOwner: deleteOwnerMutation.mutate,

    addEntity: addEntityMutation.mutate,
    updateEntity: updateEntityMutation.mutate,
    deleteEntity: deleteEntityMutation.mutate,

    addBankAccount: addBankAccountMutation.mutate,
    updateBankAccount: updateBankAccountMutation.mutate,
    deleteBankAccount: deleteBankAccountMutation.mutate,

    completeKYC: completeKYCMutation.mutate,
    isCompletingKYC: completeKYCMutation.isPending,
  };
}
